<template>
    <div class="container mx-auto">
        <div class="mt-6 flex flex-wrap items-center justify-between gap-y-7">
            <h1
                class="order-3 w-full text-center text-[36px] lg:order-2 lg:w-auto lg:text-[40px] xl:text-[50px]"
            >
                Packaging
            </h1>
            <div class="order-1 inline-flex w-1/2 lg:w-1/4">
                <a
                    href="#intro"
                    class="inline-flex gap-2 font-semibold text-grind hover:text-brew"
                >
                    <img
                        src="~/assets/images/product-finder/back-arrow.svg"
                        alt="Back arrow"
                        width="33"
                        height="24"
                    />
                    Back
                </a>
            </div>
            <div
                class="order-2 inline-flex w-1/2 justify-end lg:order-3 lg:w-1/4"
            ></div>
        </div>
        <div class="flex flex-col items-center justify-center gap-8 px-4 py-8">
            <div class="mt-4">
                <Alert
                    v-if="state.showErrors && state.continueClicked"
                    id="configurator-error"
                    type="error"
                    class="mb-8"
                    :dismissable="true"
                    slot-class="wysiwyg-content wysiwyg-compact"
                    @dismiss="$emit('dismissErrors')"
                >
                    <p>Make the following selections to continue:</p>

                    <ul>
                        <li
                            v-for="message in state.validationMessages"
                            :key="message"
                        >
                            {{ message }}
                        </li>
                    </ul>
                </Alert>
            </div>
            <h3 class="text-center text-2xl lg:w-auto">
                {{ designPricePackage.packageQuestion }}
            </h3>
            <div class="lg:px-6">
                <div
                    class="mt-4 grid grid-cols-2 justify-start gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-3"
                >
                    <ConfiguratorButtonImage
                        v-for="selection in packageSelections"
                        :key="selection.value"
                        :name="selection.value"
                        :label="selection.label"
                        :image-url="getPackageImage(selection.value)"
                        :selected="state.currentPackage === selection.value"
                        :errored="false"
                        :disabled="checkPackageIsDisabled(selection.value)"
                        :clickable-when-disabled="true"
                        height-class="lg:h-40"
                        width-class="lg:w-40"
                        :use-story-blok-image="true"
                        @click="
                            onPackageClick(
                                selection.value,
                                selection.minimumQuantityDesignLab,
                                selection.minimumQuantityUpload,
                            )
                        "
                    />
                </div>
            </div>
            <div class="col-8 mt-8 w-[100%] lg:w-[50%]">
                <h3
                    class="order-3 w-full text-center text-2xl lg:order-2 lg:w-auto"
                >
                    {{ designPricePackage.quantityQuestion }}
                </h3>
                <div
                    class="mt-6 grid grid-cols-2 gap-3 xs:grid-cols-3 sm:grid-cols-5 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
                >
                    <ConfiguratorButtonText
                        v-for="quantityBreak in quantityPackageBreaks"
                        :key="quantityBreak.value"
                        :name="`quantity-${quantityBreak.value}`"
                        :label="quantityBreak.label"
                        :selected="
                            !state.customQuantitySelected &&
                            state.currentQuantity === quantityBreak.value
                        "
                        :disabled="
                            checkQuantityIsDisabled(
                                quantityBreak.value as number,
                            )
                        "
                        :errored="false"
                        :data-cy="`quantity-${quantityBreak.value}-btn`"
                        @click="onQuantityClick(quantityBreak.value as number)"
                    />
                    <ConfiguratorButtonText
                        label="Other"
                        name="quantity-other"
                        :selected="state.customQuantitySelected"
                        data-cy="quantity-other-btn"
                        :clickable-when-disabled="true"
                        @click="onQuantityClick(null)"
                    />
                </div>
                <div class="mt-8 text-center">
                    <span class="font-semibold">*Note:</span> For large volume
                    inquiries - please
                    <a href="/contact-us" target="_blank">contact us</a>.
                </div>
            </div>
            <div class="col-8 mt-8">
                <h3
                    class="order-3 w-full text-center text-2xl lg:order-2 lg:w-auto"
                >
                    {{ designPricePackage.designOptionsQuestion }}
                    <button
                        aria-label="Learn More"
                        class="pl-2"
                        @click="onOpenModal"
                    >
                        <Icon name="question-circle" class="text-[16px]" />
                    </button>
                </h3>
                <div class="mt-6 grid grid-cols-2 gap-6">
                    <ConfiguratorButtonImage
                        v-for="selection in designPackageOptionsSelections"
                        :key="selection.value"
                        :name="selection.value"
                        :label="selection.label"
                        :sub-label="selection.subLabel"
                        :image-url="getDesignOptionsImage(selection.value)"
                        :selected="
                            state.currentDesignOption === selection.value
                        "
                        height-class="lg:h-40"
                        width-class="lg:w-40"
                        sub-label-class="text-xs block"
                        :disabled="checkDesignIsDisabled(selection.value)"
                        :clickable-when-disabled="true"
                        :errored="false"
                        :use-story-blok-image="true"
                        @click="onDesignClick(selection.value)"
                    />
                </div>
            </div>
            <div class="flex justify-center">
                <button
                    class="btn-primary mt-6 inline-flex items-center justify-center gap-2 md:order-2"
                    @click="onContinueClick()"
                >
                    Continue
                </button>
            </div>
        </div>
        <DesignOptionsModal
            ref="designOptionsModal"
            :design-options-help="designPricePackage.designOptionsHelp"
        />
    </div>
</template>

<script setup lang="ts">
import ConfiguratorButtonText from '../configurator/ConfiguratorButtonText.vue';
import { DesignPricePackageType } from '@/types/DesignPriceType';
import Alert from '~/components/Alert.vue';
import ConfiguratorButtonImage from '~/components/configurator/ConfiguratorButtonImage.vue';
import DesignOptionsModal from '~/components/design-price/DesignOptionsModal.vue';
import {
    designPackageOptionsSelections,
    packageSelections,
    quantityPackageBreaks,
} from '~/utils/designprice';
import { scrollToTop } from '~/utils/helpers';

const props = defineProps<{
    designPricePackage: DesignPricePackageType;
}>();

const state = reactive<{
    currentPackage?: string;
    currentQuantity?: number;
    customQuantitySelected: boolean;
    currentDesignOption?: string;
    minimumQuantityDesignLab: number;
    minimumQuantityUpload: number;
    showErrors: boolean;
    continueClicked: boolean;
    validationMessages: string[];
}>({
    currentPackage: undefined,
    currentQuantity: 0,
    currentDesignOption: undefined,
    minimumQuantityDesignLab: 0,
    minimumQuantityUpload: 0,
    customQuantitySelected: false,
    showErrors: false,
    continueClicked: false,
    validationMessages: [],
});

const designOptionsModal = ref<InstanceType<typeof DesignOptionsModal> | null>(
    null,
);

const checkPackageIsDisabled = (packageSelected: string) => {
    const package_selection = packageSelections.find(
        (p) => p.value === packageSelected,
    );

    if (
        state.currentDesignOption === undefined &&
        state.currentQuantity === undefined
    ) {
        return false;
    }
    if (state.currentDesignOption === undefined) {
        if (
            state.currentQuantity &&
            ((package_selection?.minimumQuantityDesignLab &&
                state.currentQuantity <
                    package_selection?.minimumQuantityDesignLab) ||
                (package_selection?.minimumQuantityDesignLab === null &&
                    state.currentQuantity <
                        package_selection?.minimumQuantityUpload))
        ) {
            return true;
        }
    }

    if (state.currentDesignOption == 'roastar_design_lab') {
        if (packageSelected === 'paper-tube' || packageSelected === 'flat') {
            return true;
        }
        if (
            state.currentQuantity &&
            state.currentQuantity < package_selection?.minimumQuantityDesignLab
        ) {
            return true;
        }
    }

    if (state.currentDesignOption == 'upload_your_own') {
        if (
            state.currentQuantity &&
            state.currentQuantity < package_selection?.minimumQuantityUpload
        ) {
            return true;
        }
    }

    return false;
};

const checkQuantityIsDisabled = (quantity: number) => {
    // For Other Quantity
    if (state.customQuantitySelected && quantity === null) {
        return false;
    }
    // For current Design Options not being set and for Current Package not being Set, enable all quantity
    if (
        state.currentDesignOption === undefined &&
        state.currentPackage === undefined
    ) {
        return false;
    }
    // If design is set Upload Your Own, use the variable minimumQuantityUpload to check if quantity is disabled
    if (state.currentDesignOption == 'upload_your_own') {
        if (state.minimumQuantityUpload > quantity) {
            return true;
        }
        // Hardcoded for now. Would need to modify this to grab the lowest possible quanttiy from the minimumQuantityUpload of each package object.
        if (quantity < 100) {
            return true;
        }
    }
    // If design is set Roastar Design Lab, use the variable minimumQuantityDesignLab to check if quantity is disabled
    if (
        state.currentDesignOption == 'roastar_design_lab' &&
        state.minimumQuantityDesignLab > quantity
    ) {
        return true;
    }
    // If current Package is set, check if minimumQuantityDesignLab quantity is set. If it is set, check if quantity is less than minimumQuantityDesignLab.
    // If it is not set, check if quantity is less than minimumQuantityUpload.
    if (
        state.currentPackage !== undefined &&
        ((state.minimumQuantityDesignLab !== null &&
            state.minimumQuantityDesignLab > quantity) ||
            (state.minimumQuantityDesignLab === null &&
                state.minimumQuantityUpload > quantity))
    ) {
        return true;
    }
    return false;
};

const checkDesignIsDisabled = (design: string) => {
    if (
        state.currentPackage === undefined &&
        state.currentQuantity === undefined
    ) {
        return false;
    }
    if (
        design == 'roastar_design_lab' &&
        (state.currentPackage === 'paper-tube' ||
            state.currentPackage === 'flat')
    ) {
        return true;
    }
    if (
        design == 'upload_your_own' &&
        state.currentPackage !== undefined &&
        state.currentQuantity !== 0 &&
        state.minimumQuantityUpload > state.currentQuantity
    ) {
        return true;
    }
    // Hardcoded for now. Would need to modify this to grab the lowest possible quanttiy from the minimumQuantityUpload of each package object.
    if (
        design == 'upload_your_own' &&
        state.currentQuantity !== 0 &&
        state.currentQuantity < 100
    ) {
        return true;
    }
    return false;
};

const onPackageClick = (
    packageSelected: string,
    minimumQuantityDesignLab: number,
    minimumQuantityUpload: number,
) => {
    state.currentPackage = packageSelected;
    state.minimumQuantityDesignLab = minimumQuantityDesignLab;
    state.minimumQuantityUpload = minimumQuantityUpload;

    if (
        state.currentDesignOption &&
        checkDesignIsDisabled(state.currentDesignOption)
    ) {
        state.currentDesignOption = undefined;
        const package_selection = packageSelections.find(
            (p) => p.value === state.currentPackage,
        );
        state.minimumQuantityDesignLab =
            package_selection?.minimumQuantityDesignLab || null;
        state.minimumQuantityUpload =
            package_selection?.minimumQuantityUpload || null;
    }

    if (
        state.currentQuantity &&
        checkQuantityIsDisabled(state.currentQuantity)
    ) {
        state.currentQuantity = 0;
    }

    onCheckErrors();
};

const onQuantityClick = (newQuantity: number | undefined) => {
    if (newQuantity) {
        // Hardcoded for now as this is the only option available for quantity < 100
        if (
            state.currentDesignOption === 'upload_your_own' &&
            newQuantity < 100
        ) {
            state.currentDesignOption = 'roastar_design_lab';
        }
        // Hardcoded as this is the only option available for quantity = 1
        if (newQuantity == 1) {
            state.currentDesignOption = 'roastar_design_lab';
            state.currentPackage = 'can';
            state.minimumQuantityDesignLab = 1;
        }
        state.currentQuantity = newQuantity;
        state.customQuantitySelected = false;

        if (
            state.currentPackage &&
            checkPackageIsDisabled(state.currentPackage)
        ) {
            state.currentPackage = undefined;
            state.minimumQuantityDesignLab = null;
            state.minimumQuantityUpload = 0;
        }
        if (
            state.currentDesignOption &&
            checkDesignIsDisabled(state.currentDesignOption)
        ) {
            state.currentDesignOption = undefined;
            const package_selection = packageSelections.find(
                (p) => p.value === state.currentPackage,
            );
            state.minimumQuantityDesignLab =
                package_selection?.minimumQuantityDesignLab || null;
            state.minimumQuantityUpload =
                package_selection?.minimumQuantityUpload || 0;
        }
    } else {
        state.currentQuantity = undefined;
        state.customQuantitySelected = true;
    }

    onCheckErrors();
};

const onDesignClick = (design: string) => {
    state.currentDesignOption = design;

    if (
        state.currentDesignOption &&
        checkPackageIsDisabled(state.currentPackage)
    ) {
        state.currentPackage = undefined;
        state.minimumQuantityDesignLab = null;
        state.minimumQuantityUpload = 0;
    }

    if (
        state.currentQuantity &&
        checkQuantityIsDisabled(state.currentQuantity)
    ) {
        state.currentQuantity = 0;
    }

    onCheckErrors();
};

const onCheckErrors = () => {
    state.showErrors = false;
    state.validationMessages = [];

    if (!state.currentPackage) {
        state.showErrors = true;
        state.validationMessages.push('Please select a package');
    }

    if (!state.currentDesignOption) {
        state.showErrors = true;
        state.validationMessages.push('Please select a design option');
    }

    if (state.currentQuantity === 0) {
        state.showErrors = true;
        state.validationMessages.push('Please select a quantity');
    }
};
const onContinueClick = () => {
    state.continueClicked = true;
    onCheckErrors();

    if (state.showErrors) {
        scrollToTop();
        return;
    }

    if (state.currentDesignOption === 'upload_your_own') {
        if (
            state.currentPackage === 'paper-tube' ||
            state.currentPackage == 'can'
        ) {
            navigateTo('/get-quote?productType=' + state.currentPackage);
        } else {
            if (state.customQuantitySelected) {
                navigateTo(
                    '/get-quote?productType=bag&style=' + state.currentPackage,
                );
            } else {
                navigateTo(
                    '/get-quote?productType=bag&style=' +
                        state.currentPackage +
                        '&quantity=' +
                        state.currentQuantity,
                );
            }
        }
    } else {
        if (state.currentPackage === 'gusset') {
            navigateTo('/design-lab/all/gusset-bag');
        } else if (state.currentPackage === 'flat-bottom') {
            navigateTo('/design-lab/all/flat-bottom-bag');
        } else if (state.currentPackage === 'can') {
            navigateTo('/design-lab/all/tin-can');
        } else if (state.currentPackage === 'stand-up') {
            navigateTo('design-lab/all/stand-up-pouch');
        } else {
            navigateTo('/design-lab');
        }
    }
};

const onOpenModal = () => {
    designOptionsModal.value?.open();
};

const getPackageImage = (packageType: string) => {
    if (packageType === 'gusset') {
        return props.designPricePackage.gussetBagImage.filename;
    } else if (packageType === 'flat-bottom') {
        return props.designPricePackage.flatBottomBagImage.filename;
    } else if (packageType === 'can') {
        return props.designPricePackage.tinCanImage.filename;
    } else if (packageType === 'paper-tube') {
        return props.designPricePackage.paperTubeImage.filename;
    } else if (packageType === 'stand-up') {
        return props.designPricePackage.standUpPouchImage.filename;
    } else if (packageType === 'flat') {
        return props.designPricePackage.flatPouchImage.filename;
    }
};

const getDesignOptionsImage = (designOption: string) => {
    if (designOption === 'roastar_design_lab') {
        return props.designPricePackage.roastarDesignLabImage.filename;
    } else if (designOption === 'upload_your_own') {
        return props.designPricePackage.uploadYourOwnImage.filename;
    }
};
</script>
